import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

export interface SignupBackgroundLayoutProps extends PropsWithChildren {
    ProductLogo: JSX.Element;
    theme?: "yellow" | "blue" | "green";
}

export const SignupBackgroundLayout = ({
    ProductLogo,
    children,
    theme
}: SignupBackgroundLayoutProps) => {
    const team26VisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    return (
        <div
            css={[
                containerStyles,
                theme === "yellow" && yellowThemeCss,
                theme === "green" && greenThemeCss
            ]}
        >
            {ProductLogo}
            <div>
                <div
                    css={[
                        formWrapperCss,
                        team26VisualUpdates && team26FormWrapperCss
                    ]}
                >
                    {children}
                </div>
            </div>
            <AtlassianLogo
                appearance="neutral"
                size="small"
                testId="atlassian-logo"
                label="Atlassian logo"
            />
        </div>
    );
};

const containerStyles = css({
    display: "grid",
    gap: "25px",
    justifyItems: "center",
    gridTemplateRows: "auto 1fr auto",
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23E9F2FE' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`,
    backgroundColor: token("elevation.surface"),
    backgroundSize: "100% 120px",
    backgroundPosition: "center 0",
    backgroundRepeat: "no-repeat",
    minHeight: "100vh",
    padding: "50px 0 100px",
    transition: "background-size 0.3s",
    [media.above.md]: {
        backgroundSize: "100% 420px"
    }
});

const yellowThemeCss = css({
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23FFF5D4' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`
});

const greenThemeCss = css({
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23D3F1A7' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`
});

const formWrapperCss = css({
    backgroundColor: token("elevation.surface"),
    borderRadius: "8px",
    boxSizing: "content-box",
    display: "flex",
    flexDirection: "column",
    margin: "0 auto",
    maxWidth: "calc(100vw - 2rem)",
    padding: "1rem",
    width: 400,
    [media.above.md]: {
        // using wac token fails vr tests
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0px 0px 8px 0px #091E4229",
        padding: "40px 80px",
        minHeight: 520
    }
});

const team26FormWrapperCss = css({
    borderRadius: 12,
    color: token("color.text.subtle"),
    fontFamily: "var(--atlassian-sans-font-family)"
});
